import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
// @mui
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
// utils

// components
import { LoadingButton } from '@mui/lab';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { visitApi } from 'src/api/visit';
import FormProvider from 'src/components/hook-form';
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';
import { Categories } from 'src/types/User';
import {
  GetVisitById,
  GetVisitByIdVisitTypeEnum,
  ProfessionalSlotsResponseDto,
} from 'woofwoof-api';
import UserUpcomingVisitCardCancelButton from './UserUpcomingVisitCardCancelButton';
import UserUpcomingVisitCardEditButton from './UserUpcomingVisitCardEditButton';
import VisitCalendar from './VisitCalendar';
import VisitCalendarSkeleton from './VisitCalendarSkeleton';
// ----------------------------------------------------------------------

export type VisitModalEditTabFormValuesProps = {
  professionalId: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  date: string;
};

type VisitModalEditTabProps = {
  onReschedule?: ({
    professionalId,
    date,
    visitId,
  }: {
    professionalId: string;
    date: string;
    visitId: string;
  }) => void;
  onClose: () => void;
  visit: GetVisitById;
  isOpen: boolean;
  isRescheduleLoading?: boolean;
  onCancel?: () => void;
  isCancelLoading?: boolean;
  isRescheduleOrCancelAvailable: boolean;
};

export default function VisitModalEditTab({
  onClose,
  visit,
  isOpen,
  onReschedule,
  isRescheduleLoading,
  onCancel,
  isCancelLoading,
  isRescheduleOrCancelAvailable,
}: VisitModalEditTabProps) {
  const { t } = useTranslation();
  const organizationId = visit.organization?.id;
  const theme = useTheme();
  const { isMd, isTabletMd, isTabletSm } = useResponsiveBreakpoints();
  const isDown515Px = useMediaQuery((theme) => theme.breakpoints.down(515));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const UpdateUserSchema: Yup.Schema<VisitModalEditTabFormValuesProps> =
    useMemo(
      () =>
        Yup.object().shape({
          professionalId: Yup.string().required(
            String(t('validation.professionalRequired')),
          ),
          firstName: Yup.string().required(
            String(t('validation.firstNameRequired')),
          ),
          lastName: Yup.string().required(
            String(t('validation.lastNameRequired')),
          ),
          avatarUrl: Yup.string(),
          date: Yup.string().required(
            String(t('validation.visitDateRequired')),
          ),
        }),
      [t],
    );

  const defaultValues = {
    professionalId: visit?.professional?.id || '',
    firstName: visit?.professional?.firstName || '',
    lastName: visit?.professional?.lastName || '',
    avatarUrl: visit?.professional?.avatar || '',
    date: visit?.startTime || '',
  };

  const resolver = useMemo(
    () => yupResolver(UpdateUserSchema),
    [UpdateUserSchema],
  );

  const methods = useForm<VisitModalEditTabFormValuesProps>({
    resolver,
    defaultValues,
  });

  const { handleSubmit, setValue, watch, formState } = methods;
  const watchedProfessionalId = watch('professionalId');
  const watchedDate = watch('date');

  const { data: slotsData, isLoading: isLoadingSlots } = useQuery(
    [
      'schedule-available-slots',
      watchedProfessionalId,
      organizationId,
      visit?.service?.duration,
    ],
    () =>
      visitApi.visitControllerGetProfessionalAvailableSlots({
        professionalId: watchedProfessionalId || '',
        category: Categories.veterinarian,
        organizationId: organizationId,
        duration: visit?.service?.duration,
      }),
    {
      enabled: !!watchedProfessionalId && isOpen,
      cacheTime: 0,
      retry: 2,
      select: (data: AxiosResponse<ProfessionalSlotsResponseDto>) => {
        return data.data.slots.map((slot: any) => ({
          date: new Date(slot),
          available: true,
        }));
      },
    },
  );

  const handleOnSelect = useCallback(
    (date: Date) => {
      const selectedDateTime = date.toISOString();

      if (selectedDateTime === watchedDate) {
        setValue('date', '');
        return;
      }

      setValue('date', date.toISOString());
    },
    [watchedDate, setValue],
  );

  const isFormDirty =
    watchedProfessionalId !== defaultValues.professionalId ||
    watchedDate !== defaultValues.date;

  const getNumOfColumnsForDisplaySize = () => {
    if (isDown515Px) {
      return 3;
    }
    if (isMd) {
      return 4;
    }
    if (isTabletSm) {
      return 5;
    }
    if (isTabletMd) {
      return 6;
    } else {
      return 6;
    }
  };

  const getActions = () => {
    if (visit.visitType === GetVisitByIdVisitTypeEnum.Video) {
      return (
        <Stack
          sx={{
            flexDirection: isDown515Px ? 'column' : 'row',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
          }}
        >
          <Stack
            flexDirection={isDown515Px ? 'column' : 'row'}
            gap={2}
            justifyContent="space-between"
            flex={1}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              fullWidth={isDown515Px}
              size={isDown515Px ? 'large' : 'medium'}
            >
              {t('common.close')}
            </Button>
            <Stack
              sx={{ flexDirection: isDown515Px ? 'column' : 'row', gap: 2 }}
            >
              {onCancel && (
                <UserUpcomingVisitCardCancelButton
                  visit={visit}
                  onCancel={onCancel}
                  isRescheduleOrCancelAvailable={isRescheduleOrCancelAvailable}
                  isCancelLoading={isCancelLoading}
                  isRescheduleLoading={isRescheduleLoading}
                  disabled={isLoadingSlots}
                  isIcon={false}
                  size={isDown515Px ? 'large' : 'medium'}
                />
              )}
              {onReschedule && (
                <UserUpcomingVisitCardEditButton
                  isIcon={false}
                  visit={visit}
                  type="submit"
                  isRescheduleOrCancelAvailable={isRescheduleOrCancelAvailable}
                  isCancelLoading={isCancelLoading}
                  isRescheduleLoading={isRescheduleLoading}
                  disabled={isLoadingSlots || !watchedDate || !isFormDirty}
                  size={isDown515Px ? 'large' : 'medium'}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack
        sx={{
          flexDirection: isDown515Px ? 'column' : 'row',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 2,
        }}
      >
        <Stack
          flexDirection={isDown515Px ? 'column' : 'row'}
          gap={2}
          justifyContent="space-between"
          flex={1}
        >
          <Stack sx={{ flexDirection: isDown515Px ? 'column' : 'row', gap: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              size={isDown515Px ? 'large' : 'medium'}
              onClick={onClose}
            >
              {t('common.close')}
            </Button>
          </Stack>
          <Stack sx={{ flexDirection: isDown515Px ? 'column' : 'row', gap: 2 }}>
            {onCancel && (
              <LoadingButton
                color="error"
                variant="outlined"
                sx={{ mt: '0px !important' }}
                onClick={onCancel}
                loading={isCancelLoading}
                fullWidth
                size={isDown515Px ? 'large' : 'medium'}
                disabled={
                  isLoadingSlots || isRescheduleLoading || isCancelLoading
                }
                data-testid="visit-cancel-open"
              >
                <span>{t('visit.cancelVisitShort')}</span>
              </LoadingButton>
            )}
            <LoadingButton
              variant="contained"
              sx={{ mt: '0px !important' }}
              type="submit"
              data-testid="visit-reschedule-save"
              loading={isRescheduleLoading}
              fullWidth
              size={isDown515Px ? 'large' : 'medium'}
              disabled={
                isLoadingSlots ||
                !watchedDate ||
                isRescheduleLoading ||
                isCancelLoading ||
                !isFormDirty
              }
            >
              <span>{t('common.save')}</span>
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  return (
    <FormProvider
      methods={methods}
      onSubmit={
        onReschedule &&
        handleSubmit((data) =>
          onReschedule({
            professionalId: data.professionalId,
            date: data.date,
            visitId: visit.id,
          }),
        )
      }
    >
      <Box rowGap={isMobile ? 2 : 3} display="grid" width="100%">
        <Divider />
        <Stack sx={{ gap: 2, minHeight: '380px' }}>
          <Typography>{t('visit.rescheduleVisitTitle')}</Typography>
          {isLoadingSlots ? (
            <VisitCalendarSkeleton numberOfSkeletonRows={7} />
          ) : (
            <VisitCalendar
              dates={slotsData || []}
              onSelect={handleOnSelect}
              disabled={isLoadingSlots || isRescheduleLoading}
              isLoading={isLoadingSlots}
              calendarColumns={getNumOfColumnsForDisplaySize()}
              numberOfSkeletonRows={14}
            />
          )}
        </Stack>
      </Box>

      {getActions()}
    </FormProvider>
  );
}
